import React, { useState } from 'react';
import skyHostLogo from '../assets/images/regenerated_image_1780444681023.jpg';
import { UserRole, Hotel } from '../types';
import { Shield, KeyRound, User, ArrowRight, Building2 } from 'lucide-react';

interface LoginProps {
  onLogin: (role: UserRole, hotelId: string, isSuper?: boolean) => void;
  hotels: Hotel[];
}

export default function Login({ onLogin, hotels }: LoginProps) {
  const [identificador, setIdentificador] = useState('');
  const [credencial, setCredencial] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const cleanUser = identificador.trim().toLowerCase();
    const cleanPass = credencial.trim();

    if (!cleanUser || !cleanPass) {
      setError('Por favor, preencha as credenciais correspondentes.');
      return;
    }

    // 1. Check Super Admin Master credentials
    if (cleanUser === 'superadmin' && cleanPass === '!Laura2306') {
      onLogin('super', '', true);
      return;
    }

    // Helper slug generator for fallback checks
    const getHotelSlug = (name: string) => {
      return name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // remove accents
        .replace(/[^a-z0-9]/g, "") // remove non-alphanumeric
        .substring(0, 15);
    };

    // 2. Scan memory database for any custom configured credentials on hotels
    for (const h of hotels) {
      const slug = getHotelSlug(h.name);
      
      // A. Custom admin credentials configured inside "Gerenciar Hotéis"
      const configuredAdminUser = h.adminUser?.trim().toLowerCase() || `admin_${slug}`;
      const configuredAdminPass = h.adminPass || 'admin';

      // B. Custom receptionist credentials
      const configuredRecepUser = h.recepUser?.trim().toLowerCase() || `recep_${slug}`;
      const configuredRecepPass = h.recepPass || 'recep';

      if (cleanUser === configuredAdminUser) {
        if (!h.active) {
          setError(`A unidade "${h.name}" está temporariamente desativada.`);
          return;
        }
        if (cleanPass === configuredAdminPass) {
          onLogin('administrador', h.id, false);
          return;
        } else {
          setError('Código de acesso incorreto para o Administrador desta unidade.');
          return;
        }
      }

      if (cleanUser === configuredRecepUser) {
        if (!h.active) {
          setError(`A unidade "${h.name}" está temporariamente desativada.`);
          return;
        }
        if (cleanPass === configuredRecepPass) {
          onLogin('recepcionista', h.id, false);
          return;
        } else {
          setError('Código de acesso incorreto para a Recepção desta unidade.');
          return;
        }
      }
    }

    // 3. Sequential index fallback (e.g. admin1/admin, recep2/recep) for backward compatibility
    const seqMatch = cleanUser.match(/^(admin|recep)(\d+)$/);
    if (seqMatch) {
      const type = seqMatch[1]; // admin or recep
      const idx = parseInt(seqMatch[2], 10) - 1; // 0-indexed position
      const targetHotel = hotels[idx];

      if (targetHotel) {
        if (!targetHotel.active) {
          setError(`A unidade "${targetHotel.name}" está desativada.`);
          return;
        }

        const requiredPass = type === 'admin' ? (targetHotel.adminPass || 'admin') : (targetHotel.recepPass || 'recep');
        if (cleanPass === requiredPass) {
          onLogin(type === 'admin' ? 'administrador' : 'recepcionista', targetHotel.id, false);
          return;
        } else {
          setError(`Código de acesso incorreto para o ${type === 'admin' ? 'Administrador' : 'Recepcionista'} do hotel ${targetHotel.name}.`);
          return;
        }
      }
    }

    // 4. Ultimate fallback for standard 'admin' or 'recep' (defaults to first active hotel)
    if (cleanUser === 'admin' || cleanUser === 'recep') {
      const firstActive = hotels.find(h => h.active);
      if (firstActive) {
        const requiredPass = cleanUser === 'admin' ? (firstActive.adminPass || 'admin') : (firstActive.recepPass || 'recep');
        if (cleanPass === requiredPass) {
          onLogin(cleanUser === 'admin' ? 'administrador' : 'recepcionista', firstActive.id, false);
          return;
        }
      }
    }

    setError('Credenciais inválidas. Verifique os dados cadastrados na Gestão do Hotel.');
  };

  return (
    <div id="login-container" className="min-h-screen bg-zinc-950 flex flex-col justify-start md:justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Upper Brand / Welcome */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-6 flex flex-col items-center">
        <img 
          src={skyHostLogo} 
          alt="SkyHost" 
          className="w-64 sm:w-72 h-auto mix-blend-lighten" 
          referrerPolicy="no-referrer"
        />
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md space-y-4">
        {/* Core Login Form Card */}
        <div className="bg-zinc-900 py-8 px-6 sm:px-10 shadow-lg shadow-black/40 border border-zinc-800 rounded-2xl">
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Identificador Input */}
            <div>
              <label htmlFor="login-id-input" className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                Identificador
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                  <User className="w-4 h-4" />
                </span>
                <input
                  id="login-id-input"
                  type="text"
                  required
                  autoComplete="off"
                  value={identificador}
                  onChange={(e) => setIdentificador(e.target.value)}
                  className="block w-full pl-9 pr-3 py-2 text-xs border border-zinc-700 bg-zinc-950 rounded-xl focus:bg-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-500 focus:border-zinc-500 transition-colors placeholder-zinc-600 text-zinc-100"
                />
              </div>
            </div>

            {/* Credencial Input */}
            <div>
              <label htmlFor="login-key-input" className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                Código de Acesso
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                  <KeyRound className="w-4 h-4" />
                </span>
                <input
                  id="login-key-input"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={credencial}
                  onChange={(e) => setCredencial(e.target.value)}
                  className="block w-full pl-9 pr-3 py-2 text-xs border border-zinc-700 bg-zinc-950 rounded-xl focus:bg-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-500 focus:border-zinc-500 transition-colors placeholder-zinc-600 text-zinc-100"
                />
              </div>
            </div>

            {error && (
              <div id="login-error-box" className="p-2.5 bg-red-950/40 border border-red-900/50 rounded-xl text-red-400 text-xs text-center font-medium leading-relaxed">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                id="btn-login-submit"
                className="w-full flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-xl bg-white text-zinc-950 text-xs font-bold uppercase tracking-wider hover:bg-zinc-200 transition-all cursor-pointer shadow-sm active:scale-[0.98]"
              >
                Autenticar e Entrar
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </form>
          

        </div>
      </div>
    </div>
  );
}
